import { Box, Button, Field, Flex, HStack, Text, Textarea } from '@chakra-ui/react';
import { Bookmark, InfoCircle } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { useInvitationActions, useInvitationStoreSelector } from '@/contexts/invitation-context';
import { DoDontCards } from '@/pages/collabs/brief-templates/editor/sections/guidelines/DoDontCards';

interface CollabDraftStepProps {
  onClose: () => void;
}

/**
 * Blank-brief drafting step (sponsored path): the sections COMMON to every
 * proposal issued from this brief — concept and do's/don'ts — plus the "also
 * save as a template" toggle. Per-recipient terms (deliverables, compensation,
 * timeline) come on the next step. Reference-file uploads are not part of the
 * wizard v1 (they require a template to attach to).
 */
const CollabDraftStep = ({ onClose }: CollabDraftStepProps) => {
  const { t } = useTranslation();
  const collabContent = useInvitationStoreSelector((state) => state.collabContent);
  const saveAsTemplate = useInvitationStoreSelector((state) => state.saveAsTemplate);
  const { updateCollabContent, setSaveAsTemplate, setCurrentView } = useInvitationActions();

  if (!collabContent) return null;

  return (
    <Flex direction="column" gap="3">
      <HStack gap="2" color="fg.muted" align="flex-start">
        <InfoCircle width={14} height={14} style={{ flexShrink: 0, marginTop: 2 }} />
        <Text fontSize="11.5px" lineHeight="1.5">
          {t('invitations.modal.collab.wizard.draft.commonNote')}
        </Text>
      </HStack>

      <Field.Root>
        <Field.Label fontFamily="heading" fontSize="12.5px" fontWeight="semibold">
          {t('invitations.modal.collab.wizard.draft.conceptLabel')}
        </Field.Label>
        <Textarea
          variant="outline"
          rows={4}
          placeholder={t('invitations.modal.collab.wizard.draft.conceptPlaceholder')}
          value={collabContent.concept.body}
          onChange={(e) =>
            updateCollabContent((prev) => ({ ...prev, concept: { body: e.target.value } }))
          }
        />
      </Field.Root>

      <DoDontCards
        dos={collabContent.guidelines.dos}
        donts={collabContent.guidelines.donts}
        onDosChange={(dos) =>
          updateCollabContent((prev) => ({ ...prev, guidelines: { ...prev.guidelines, dos } }))
        }
        onDontsChange={(donts) =>
          updateCollabContent((prev) => ({ ...prev, guidelines: { ...prev.guidelines, donts } }))
        }
      />

      <HStack
        gap="2.5"
        align="flex-start"
        bg="background"
        borderWidth="1px"
        borderColor="cardBorder"
        rounded="lg"
        px="14px"
        py="10px"
      >
        <Box color="primary.500" flex="none" mt="0.5">
          <Bookmark width={15} height={15} />
        </Box>
        <Box flex="1">
          <Text fontFamily="heading" fontSize="12.5px" fontWeight="semibold" color="dark">
            {t('invitations.modal.collab.wizard.draft.saveAsTemplate')}
          </Text>
          <Text fontSize="11.5px" color="fg.muted">
            {t('invitations.modal.collab.wizard.draft.saveAsTemplateHelp')}
          </Text>
        </Box>
        <Switch
          checked={saveAsTemplate}
          onChange={() => setSaveAsTemplate(!saveAsTemplate)}
          colorPalette="primary"
          size="sm"
        />
      </HStack>

      <Flex justify="flex-end" gap="2" pt="1">
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button colorPalette="primary" onClick={() => setCurrentView('collabTerms')}>
          {t('invitations.modal.collab.wizard.draft.cta')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default CollabDraftStep;
